import { useState } from 'react';
import getFreeSpots from '../utils/getFreeSpots';
import getStringTimeInSec from '../utils/getStringTimeInSec';
import shortDates from '../utils/shortDates';
import getExceptionSlots from '../utils/getExceptionSlots';

const HOST = `https://1aof2q6mzd.execute-api.eu-central-1.amazonaws.com/v3/booking-app/`;
const SHOP_DOMAIN = 'kyro-distillery-home.myshopify.com';

function useBooking(eventTypes) {
  const [participants, setParticipants] = useState({
    tasting: [1],
    tour: [1],
    custom: [1],
  });
  const [maxParticipants, setMaxParticipants] = useState(0);
  const [bookingEvents, setBookingEvents] = useState([]);
  const [availableEvents, setAvailableEvents] = useState({});
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);

  const initBooking = (products) => {
    const eventKeys = {
      'Distillery tour': 'tour',
      Tasting: 'tasting',
      Custom: 'custom',
    };

    const formattedEvents = eventTypes.length
      ? eventTypes.map((e) => eventKeys[e])
      : [];

    const participantCounts = {};

    const evts = products.reduce((acc, curr) => {
      if (curr.metafield && curr.metafield.booking && curr.metafield.active) {
        if (formattedEvents.includes(curr.metafield.eventType)) {
          const paidVariant = curr.variants?.edges?.find(
            (e) => !!parseFloat(e.node.price.amount)
          );
          const freeVariant =
            curr.variants?.edges?.find(
              (e) => parseFloat(e.node.price.amount) === 0
            ) || null;
          const price = paidVariant
            ? parseFloat(paidVariant.node.price.amount)
            : null;
          participantCounts[curr.metafield.eventType] = Math.min(
            curr.metafield.freeSpots,
            curr.metafield.maxOrderItems
          );
          return [
            ...acc,
            {
              name:
                curr.metafield.eventType === 'custom'
                  ? curr.title
                  : curr.metafield.eventType === 'tour'
                  ? 'Distillery tour'
                  : 'Tasting',
              value: curr.metafield.eventType,
              freeSpots: curr.metafield.freeSpots,
              selected: false,
              metafield: curr.metafield,
              paidProductId: paidVariant.node.id,
              prepaidProductId: freeVariant?.node?.id,
              price,
              id: curr.id,
            },
          ];
        }
      }
      return acc;
    }, []);

    resolveSlots();
    setBookingEvents(evts);
    setMaxParticipants(participantCounts);
    setLoading(false);
  };

  const updateBookingEvents = (evts) => {
    setBookingEvents(evts);
  };

  const resolveAvailableEvents = ({ event, participantAmount, week = [] }) => {
    if (event) {
      setParticipants({ ...participants, [event]: participantAmount });
    }

    const selectedEvents = bookingEvents.filter((e) => e.selected);

    const weekData = week.reduce((events, weekday) => {
      const now = new Date(weekday.date);
      const time = now.getTime();
      const selectedTime = new Date(now.getTime());
      const selectedDate = selectedTime.getDay();
      let eventsOpen = true;
      const openEvents = [];

      const selectedTimeInMidnight = new Date(selectedTime.getTime());
      const selectedTimeInMidnightTime = selectedTimeInMidnight.getTime();

      let selectedEventsAvailableBookings = JSON.parse(
        JSON.stringify(selectedEvents)
      );

      for (let i = 0; i < selectedEventsAvailableBookings.length; i++) {
        if (selectedEventsAvailableBookings[i].metafield.closedDates.length) {
          for (
            let j = 0;
            j < selectedEventsAvailableBookings[i].metafield.closedDates.length;
            j++
          ) {
            const dateStrParts =
              selectedEventsAvailableBookings[i].metafield.closedDates[j].split(
                '.'
              );
            const closed = new Date(
              parseInt(dateStrParts[2]),
              parseInt(dateStrParts[1]) - 1,
              parseInt(dateStrParts[0])
            );

            // selectedTimeInMidnight.setHours(0, 0, 0, 0);
            if (closed.getTime() === selectedTimeInMidnightTime) {
              eventsOpen = false;
              break;
            }
          }
        }

        if (selectedEvents[i].metafield.booking.length) {
          for (let k = 0; k < selectedEvents[i].metafield.booking.length; k++) {
            if (selectedEvents[i].metafield.booking[k].start) {
              if (
                selectedEvents[i].metafield.booking[k].start >
                selectedTimeInMidnightTime
              ) {
                const availableBookings =
                  selectedEventsAvailableBookings[i].metafield.booking;
                selectedEventsAvailableBookings[i].metafield.booking =
                  availableBookings.filter(
                    (b) => b.start <= selectedTimeInMidnightTime
                  );
              }
            }
            if (selectedEvents[i].metafield.booking[k].end) {
              if (
                selectedEvents[i].metafield.booking[k].end <
                selectedTimeInMidnightTime
              ) {
                const availableBookings =
                  selectedEventsAvailableBookings[i].metafield.booking;
                selectedEventsAvailableBookings[i].metafield.booking =
                  availableBookings.filter(
                    (b) => b.end >= selectedTimeInMidnightTime
                  );
              }
            }
          }
        }

        // find out matching slots for the same date
        const slotStartTime = Math.floor(time / 1000);
        const endTime = new Date(time);
        endTime.setHours(23, 59, 59, 59);
        const slotEndTime = Math.floor(endTime.getTime() / 1000);
        const currentSlots = slots.filter(
          (s) =>
            s.event ===
              (selectedEventsAvailableBookings[i].value === 'custom'
                ? selectedEventsAvailableBookings[i].value +
                  '-' +
                  selectedEventsAvailableBookings[i].metafield.identifier
                : selectedEventsAvailableBookings[i].value) &&
            s.slot <= slotEndTime &&
            s.slot >= slotStartTime
        );

        // find if we have matching time with enough booking spots
        if (currentSlots.length) {
          currentSlots.forEach((cs) => {
            openEvents.push({
              event: selectedEventsAvailableBookings[i],
              slot: cs.slot,
            });
          });
        } else {
          openEvents.push({
            event: selectedEventsAvailableBookings[i],
            slot: null,
          });
        }
      }
      if (!eventsOpen || !openEvents.length) {
        return { ...events, [time]: null };
      }

      // This causes duplicate event issues, maybe should be selecedEvents and not openEvents
      const selectedDayData = selectedEventsAvailableBookings.reduce(
        (acc, curr, i) => {
          const currDateTimeSlots = curr.metafield.booking.reduce(
            (final, next) => {
              const batch = next.slots[shortDates[selectedDate]];
              return [...new Set([...final, ...batch])]
                .filter((timeStr) => {
                  const [hours, minutes] = timeStr.match(/\d+/g);
                  const dd = new Date(time);
                  // + 3 was a quick fix for time zone offset showing too many items (GMT+0300 Eastern European Summer Time)
                  dd.setHours(parseInt(hours) + 3, parseInt(minutes));
                  const timestamp = Math.floor(dd.getTime() / 1000);
                  const currentSlot = slots.find(
                    (s) =>
                      s.slot === timestamp &&
                      s.event ===
                        (curr.value == 'custom'
                          ? curr.value + '-' + curr.metafield.identifier
                          : curr.value)
                  );

                  if (currentSlot) {
                    const availableSpots =
                      curr.freeSpots - currentSlot.bookings;
                    const participantsToCompare =
                      participantAmount?.length &&
                      Array.isArray(participantAmount)
                        ? participantAmount[0]
                        : null;

                    if (participantsToCompare) {
                      return availableSpots >= participantsToCompare;
                    }

                    if (curr !== undefined && curr.value) {
                      return availableSpots >= participants[curr.value][0];
                    }

                    return false;
                  }
                  return true;
                })
                .sort((a, b) => {
                  const aSec = getStringTimeInSec(a);
                  const bSec = getStringTimeInSec(b);
                  return aSec - bSec;
                });
            },
            []
          );
          if (!currDateTimeSlots.length) return acc;

          return [
            ...acc,
            {
              slots: currDateTimeSlots,
              smallestTimeSec: getStringTimeInSec(currDateTimeSlots[0]),
              exceptions: curr.metafield.exceptions,
              eventType:
                curr.metafield.eventType === 'custom'
                  ? curr.metafield.eventType + '-' + curr.metafield.identifier
                  : curr.metafield.eventType,
              eventTitle: curr.name,
              freeSpots: curr.metafield.freeSpots,
            },
          ];
        },
        []
      );

      selectedDayData.sort((a, b) => a.smallestTimeSec - b.smallestTimeSec);

      if (!selectedDayData.length) {
        return { ...events, [time]: null };
      }
      const ref = selectedDayData.shift();

      const final = ref.slots.reduce((acc, curr) => {
        ref.freeSpots = getExceptionSlots(ref, time);
        const freeSpots = getFreeSpots(
          curr,
          now,
          ref.freeSpots,
          slots,
          ref.eventType
        );
        return {
          ...acc,
          [curr]: {
            data: [
              {
                eventType: ref.eventType,
                eventTitle: ref.eventTitle,
                freeSpots,
                slot: curr,
              },
            ],
          },
        };
      }, {});

      if (selectedDayData.length) {
        const keys = Object.keys(final);
        const secKeys = keys.map((k) => getStringTimeInSec(k));

        selectedDayData.forEach((d) => {
          if (keys.length === d.slots.length) {
            d.slots.forEach((s, idx) => {
              const sec = getStringTimeInSec(s);
              // Get the first key smaller
              let smallestKeyIndex = secKeys.reduce((acc, curr) => {
                if (secKeys[acc] < sec && secKeys[curr] < secKeys[acc]) {
                  return curr;
                }
                return acc;
              }, 0);

              smallestKeyIndex =
                smallestKeyIndex >= idx ? smallestKeyIndex : idx;

              d.freeSpots = getExceptionSlots(d, time);

              const freeSpots = getFreeSpots(
                s,
                now,
                d.freeSpots,
                slots,
                d.eventType
              );

              const hasSameSlot = final[keys[smallestKeyIndex]].data.find(
                (d) => d.slot === s
              );
              if (!hasSameSlot) {
                final[keys[smallestKeyIndex]].data.push({
                  eventType: d.eventType,
                  eventTitle: d.eventTitle,
                  freeSpots,
                  slot: s,
                });
              }
            });
          } else {
            const s = d.slots[0];

            d.freeSpots = getExceptionSlots(d, time);

            const freeSpots = getFreeSpots(
              s,
              now,
              d.freeSpots,
              slots,
              d.eventType
            );

            keys.forEach((k) => {
              const sameSlot = final[k].data.find((d) => d.slot === s);

              if (!sameSlot) {
                final[k].data.push({
                  eventType: d.eventType,
                  eventTitle: d.eventTitle,
                  freeSpots: freeSpots,
                  slot: s,
                });
              }
            });
          }
        });
      }
      return { ...events, [time]: final };
    }, {});

    setAvailableEvents(weekData);
  };

  const resolveSlots = async () => {
    try {
      // setLoading(true);
      const res = await fetch(`${HOST}booking-slots`, {
        method: 'get',
        headers: {
          'x-shop': SHOP_DOMAIN,
        },
      });
      if (res.status < 300) {
        const data = await res.json();
        setSlots(data);
      }
      // TODO: IF We do not get free slots, show error and prevent booking!
    } catch (e) {
      console.error('ERROR: ', e);
    } finally {
      setLoading(false);
    }
  };

  return {
    initBooking,
    maxParticipants,
    bookingEvents,
    updateBookingEvents,
    participants,
    availableEvents,
    resolveAvailableEvents,
    slots,
    resolveSlots,
    loading,
  };
}

export default useBooking;
